/**
 * Schedule calendar helpers (shared with Schedule Builder preview logic).
 * Student-perspective day states for tracks / learning periods + exceptions.
 */

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const WEEKDAY_HEADER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const STATE_COLOR = {
  session: { bg: '#dcfce7', border: '#86efac', text: '#166534' },
  added: { bg: '#fef9c3', border: '#fde047', text: '#854d0e' },
  removed: { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
  off: { bg: '#ffffff', border: '#f1f5f9', text: '#cbd5e1' }
};

export const SCOPE_COLOR = {
  all: '#64748b',
  students: '#dc2626',
  staff: '#b45309'
};

export function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function fmtLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export function isoWeekday(d) {
  const w = d.getDay();
  return w === 0 ? 7 : w;
}

export function enumerateMonths(start, end) {
  const months = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= last) {
    months.push({ year: cur.getFullYear(), month: cur.getMonth() });
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

function collectAncestorExceptions(period, allPeriods) {
  if (!period) return [];
  const out = [];
  let cur = period;
  let depth = 1;
  const seen = new Set();
  while (cur && cur.parent_id) {
    if (seen.has(cur.parent_id)) break;
    seen.add(cur.parent_id);
    const parent = allPeriods.find((p) => p.id === cur.parent_id);
    if (!parent) break;
    for (const p of allPeriods) {
      if (
        p.parent_id === parent.id &&
        (p.type === 'exception_add' || p.type === 'exception_remove')
      ) {
        out.push({ raw: p, depth });
      }
    }
    cur = parent;
    depth += 1;
  }
  return out;
}

function shapeExceptionRow(raw, depth = 0) {
  return {
    id: raw.id,
    parent_id: raw.parent_id,
    name: raw.name,
    type: raw.type,
    start: parseLocalDate(raw.start_date),
    end: parseLocalDate(raw.end_date),
    meeting_days: raw.meeting_days || [],
    sort_order: raw.sort_order || 0,
    scope: raw.scope || 'all',
    depth
  };
}

export function effectiveExceptionsFor(period, allPeriods) {
  if (!period) return [];
  const ownPairs = (allPeriods || [])
    .filter(
      (p) =>
        p.parent_id === period.id &&
        (p.type === 'exception_add' || p.type === 'exception_remove')
    )
    .map((p) => ({ raw: p, depth: 0 }));
  const inherited = collectAncestorExceptions(period, allPeriods || []);
  return [...ownPairs, ...inherited]
    .map(({ raw, depth }) => shapeExceptionRow(raw, depth))
    .filter((p) => p.start && p.end);
}

export function getDayDetail(date, baseMeetingDays, exceptions, forRole = null) {
  const wd = isoWeekday(date);
  const baseDays =
    baseMeetingDays && baseMeetingDays.length > 0 ? baseMeetingDays : [1, 2, 3, 4, 5, 6, 7];
  const isBaseSession = baseDays.includes(wd);

  const applicable = [];
  for (const ex of exceptions || []) {
    if (!ex.start || !ex.end) continue;
    if (date < ex.start || date > ex.end) continue;
    const exDays = ex.meeting_days && ex.meeting_days.length > 0 ? ex.meeting_days : null;
    if (exDays && !exDays.includes(wd)) continue;
    if (forRole && forRole !== 'all' && ex.scope && ex.scope !== 'all' && ex.scope !== forRole) {
      continue;
    }
    applicable.push(ex);
  }

  if (applicable.length === 0) {
    return { state: isBaseSession ? 'session' : 'off', scope: 'all' };
  }

  const priorityOf = (e) => (10 - (e.depth ?? 0)) * 100 + (e.sort_order ?? 0);
  applicable.sort((a, b) => {
    const ap = priorityOf(a);
    const bp = priorityOf(b);
    if (ap !== bp) return bp - ap;
    if (a.type !== b.type) return a.type === 'exception_remove' ? -1 : 1;
    return 0;
  });

  const winner = applicable[0];
  const winnerScope = winner.scope || 'all';
  if (winner.type === 'exception_remove') {
    return { state: isBaseSession ? 'removed' : 'off', scope: winnerScope };
  }
  return { state: isBaseSession ? 'session' : 'added', scope: winnerScope };
}

function defaultMeetingDays(period) {
  return period?.meeting_days && period.meeting_days.length > 0
    ? period.meeting_days
    : [1, 2, 3, 4, 5, 6, 7];
}

/**
 * Build calendar data for a learning period (student perspective), including
 * inherited exceptions and optional parent-track overlay days.
 */
export function buildLearningPeriodCalendarSection(learningPeriodId, allPeriods) {
  const list = allPeriods || [];
  const lp = list.find((p) => String(p.id) === String(learningPeriodId) && p.type === 'learning');
  if (!lp) return null;

  const rangeStart = parseLocalDate(lp.start_date);
  const rangeEnd = parseLocalDate(lp.end_date);
  if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return null;

  const meetingDays = defaultMeetingDays(lp);
  const exceptions = effectiveExceptionsFor(lp, list);

  let overlay = null;
  const parentTrack = lp.parent_id
    ? list.find((p) => p.id === lp.parent_id && p.type === 'track')
    : null;
  if (parentTrack) {
    overlay = {
      start: parseLocalDate(parentTrack.start_date),
      end: parseLocalDate(parentTrack.end_date),
      meeting_days: defaultMeetingDays(parentTrack)
    };
  }

  const months = enumerateMonths(rangeStart, rangeEnd);
  const dayStates = new Map();
  const dayScopes = new Map();
  const overlayDays = new Set();
  let session = 0;
  let added = 0;
  let removed = 0;

  const cur = new Date(rangeStart);
  while (cur <= rangeEnd) {
    const key = fmtLocalDate(cur);
    const detail = getDayDetail(cur, meetingDays, exceptions, 'students');
    const state = detail.state;
    dayStates.set(key, state);
    if (detail.scope && detail.scope !== 'all') dayScopes.set(key, detail.scope);
    if (state === 'session') session += 1;
    else if (state === 'added') added += 1;
    else if (state === 'removed') removed += 1;

    if (overlay?.start && overlay?.end && cur >= overlay.start && cur <= overlay.end) {
      const wd = isoWeekday(cur);
      if (overlay.meeting_days.includes(wd)) overlayDays.add(key);
    }
    cur.setDate(cur.getDate() + 1);
  }

  return {
    id: lp.id,
    title: lp.name || lp.title || 'Learning period',
    rangeStart,
    rangeEnd,
    months,
    dayStates,
    dayScopes,
    overlayDays,
    counts: { session, added, removed }
  };
}
